stimulusApp.register(
  "search--travel-date-picker",
  class extends Stimulus.Controller {
    static targets = [
      "pane",
      "calendar",
      "firstMonthGroup",
      "lastMonthGroup",
      "stickyWeekdays",
      "mobileDateContainer",
      "heading",
      "mobileCheckinField",
      "mobileCheckoutField",
      "mobileCheckinLabel",
      "mobileCheckoutLabel",
      "mobileCheckinValue",
      "mobileCheckoutValue",
      "mobileCheckinPlaceholder",
      "mobileCheckoutPlaceholder",
      "checkinField",
      "checkoutField",
      "checkinLabel",
      "checkoutLabel",
      "checkinValue",
      "checkoutValue",
      "checkinInput",
      "checkoutInput",
      "prevButton",
      "nextButton",
    ]

    static values = {
      months: Array,
      monthsAbbreviated: Array,
      dayNames: Array,
      dayNamesMedium: Array,
      minCheckin: String,
      maxLeadDays: Number,
      maxStageDuration: Number,
      datePattern: String,
      locale: String,
      maxNightsLabel: String,
      nights: Object,
      checkin: String,
      checkout: String,
      checkinCursor: { type: Boolean, default: true },
      activeTab: { type: String, default: "checkin" },
      checkoutConfirmed: Boolean,
      monthStart: String,
      mobileOverlay: { type: Boolean, default: false },
      datesLabel: String,
    }
    #renderScheduled = false
    #renderToken = 0
    #deferredWorkArmed = false
    #headingScheduled = false

    connect() {
      const checkin = HB.UTCDate.parse(this.checkinInputTarget.value)
      const checkout = HB.UTCDate.parse(this.checkoutInputTarget.value)
      this.checkinValue = checkin ? HB.UTCDate.formatIso(checkin) : ""
      this.checkoutValue = checkout ? HB.UTCDate.formatIso(checkout) : ""
      this.checkoutConfirmedValue = Boolean(checkout)
      const monthStart = HB.UTCDate.firstOfMonth(
        checkin || this.#minCheckinDate(),
      )
      this.monthStartValue = HB.UTCDate.formatIso(monthStart)
      this.activeTabValue = this.checkinCursorValue ? "checkin" : "checkout"
    }

    checkinValueChanged() {
      this.#syncInputs()
      this.#scheduleRender()
    }

    checkoutValueChanged() {
      this.#syncInputs()
      this.#scheduleRender()
    }

    checkinCursorValueChanged() {
      this.#syncMobileFields(this.#checkinDate(), this.#checkoutDate())
      this.#scheduleRender()
    }

    activeTabValueChanged() {
      this.element.dispatchEvent(
        new CustomEvent("hb:search-form:datepicker-tab-change", {
          bubbles: true,
          detail: { field: this.activeTabValue || "checkin" },
        }),
      )
    }

    checkoutConfirmedValueChanged() {
      this.#scheduleRender()
    }

    monthStartValueChanged() {
      this.#scheduleRender()
    }

    open(field) {
      this.#flushHeading()
      const requestedTab = field || this.activeTabValue || "checkin"
      const shouldSelectCheckin =
        requestedTab !== "checkout" || !this.canSelectCheckout()
      this.checkinCursorValue = shouldSelectCheckin
      this.activeTabValue = shouldSelectCheckin ? "checkin" : "checkout"
      this.paneTarget.classList.add("place-search-expanded_opened")
      if (this.#isOverlayPane()) {
        this.paneTarget.classList.add("overlay_opened")
      }
      this.#postTitleToNative()
      this.#flushRender()
      this.#scrollToSelectedMonth()
    }

    close() {
      this.paneTarget.classList.remove("place-search-expanded_opened")
      if (this.#isOverlayPane()) {
        this.paneTarget.classList.remove("overlay_opened")
      }
    }

    setDates(checkin, checkout) {
      const checkinDate = HB.UTCDate.parse(checkin)
      const checkoutDate = HB.UTCDate.parse(checkout)
      this.checkinValue = checkinDate ? HB.UTCDate.formatIso(checkinDate) : ""
      this.checkoutValue = checkoutDate
        ? HB.UTCDate.formatIso(checkoutDate)
        : ""
      this.checkoutConfirmedValue = Boolean(checkoutDate)
      if (checkinDate) {
        this.monthStartValue = HB.UTCDate.formatIso(
          HB.UTCDate.firstOfMonth(checkinDate),
        )
      }
    }

    dates() {
      return {
        checkin: this.checkinInputTarget.value,
        checkout: this.checkoutInputTarget.value,
      }
    }

    summary() {
      const nights = HB.UTCDate.distanceInNights(
        this.#checkinDate(),
        this.#checkoutDate(),
      )
      return {
        checkin: this.checkinValueTarget.innerHTML,
        checkout: this.checkoutValueTarget.innerHTML,
        nights: this.#formatNights(nights),
      }
    }

    select(event) {
      const cell = event.target.closest("[data-date]")
      if (!cell) return
      event.stopPropagation()
      event.preventDefault()
      const date = HB.UTCDate.parse(cell.dataset.date)
      if (date) this.#selectDate(date)
    }

    prev(event) {
      this.#navigate(event, -1)
    }

    next(event) {
      this.#navigate(event, 1)
    }

    focusCheckin(event) {
      event?.preventDefault?.()
      this.checkinCursorValue = true
      this.activeTabValue = "checkin"
    }

    focusCheckout(event) {
      event?.preventDefault?.()
      if (!this.canSelectCheckout()) return
      this.checkinCursorValue = false
      this.activeTabValue = "checkout"
    }

    canSelectCheckout() {
      return Boolean(this.#checkinDate())
    }

    requestClose() {
      this.element.dispatchEvent(
        new CustomEvent("hb:search-form:close", { bubbles: true }),
      )
    }

    #scheduleRender() {
      this.#renderToken += 1
      if (this.#renderScheduled) return
      this.#renderScheduled = true
      if (this.#overlayHidden()) {
        this.#armDeferredWork()
        return
      }
      queueMicrotask(() => this.#flushRender())
    }

    #flushRender() {
      if (!this.#renderScheduled) return
      this.#renderScheduled = false
      this.#renderCalendar()
    }

    #overlayHidden() {
      return (
        this.mobileOverlayValue &&
        this.hasPaneTarget &&
        !this.paneTarget.classList.contains("place-search-expanded_opened")
      )
    }

    #armDeferredWork() {
      if (this.#deferredWorkArmed) return
      this.#deferredWorkArmed = true
      this.#whenIdle(() => {
        this.#deferredWorkArmed = false
        if (!this.element.isConnected) return
        if (!this.#overlayHidden()) return
        this.#runDeferredWork(this.#renderToken)
      })
    }

    #whenIdle(callback) {
      const idle =
        window.requestIdleCallback || ((task) => setTimeout(task, 200))
      idle(callback, { timeout: 1000 })
    }

    #yieldToMain() {
      const scheduler = window.scheduler
      if (scheduler?.yield) return scheduler.yield()
      if (scheduler?.postTask) {
        return scheduler.postTask(() => {}, { priority: "user-visible" })
      }
      return new Promise((resolve) => setTimeout(resolve, 0))
    }

    async #runDeferredWork(token) {
      this.#flushHeading()
      if (!this.#renderScheduled || !this.hasCalendarTarget) return
      const context = this.#renderContext()
      const sections = []
      for (const monthStart of this.#availableMonths()) {
        sections.push(this.#buildMonthSection(monthStart, context))
        await this.#yieldToMain()
        if (!this.#renderScheduled) return
        if (token !== this.#renderToken) {
          this.#armDeferredWork()
          return
        }
      }
      this.#renderScheduled = false
      this.#commitMobileCalendar(context, sections)
    }

    #renderCalendar() {
      if (!this.hasCalendarTarget) return
      if (this.mobileOverlayValue) {
        this.#renderMobileCalendar(this.#renderContext())
        return
      }
      const monthStart = this.#monthStartDate()
      if (!monthStart) return
      const context = this.#renderContext()
      if (this.hasFirstMonthGroupTarget) {
        this.#renderMonthGroup(this.firstMonthGroupTarget, monthStart, context)
      }
      if (this.hasLastMonthGroupTarget) {
        const nextMonth = HB.UTCDate.addMonths(monthStart, 1)
        this.#renderMonthGroup(this.lastMonthGroupTarget, nextMonth, context)
      }
      this.#syncNav(monthStart)
      window.HB?.Tooltip?.init?.()
    }

    #renderContext() {
      const checkin = this.#checkinDate()
      const checkinCursor = this.checkinCursorValue
      const maxStage = this.maxStageDurationValue || 0
      const minCheckin = this.#minCheckinDate()
      const maxCheckout = HB.UTCDate.addDays(
        minCheckin,
        this.maxLeadDaysValue || 0,
      )
      const context = {
        checkin,
        checkout: this.#checkoutDate(),
        checkinCursor,
        checkoutConfirmed: this.checkoutConfirmedValue,
        maxStage,
        availableFrom: null,
        availableTo: null,
        availableToInclusive: false,
        maxCheckoutByStage: null,
        maxNightsTooltip: null,
      }
      if (checkinCursor) {
        context.availableFrom = minCheckin
        context.availableTo = maxCheckout
        return context
      }
      if (!checkin) return context
      context.maxCheckoutByStage = HB.UTCDate.addDays(checkin, maxStage)
      context.availableFrom = HB.UTCDate.addDays(checkin, 1)
      context.availableTo =
        context.maxCheckoutByStage < maxCheckout
          ? context.maxCheckoutByStage
          : maxCheckout
      context.availableToInclusive = true
      if (maxStage) {
        const label =
          this.maxNightsLabelValue || "Maximum stay length: %{nights}"
        context.maxNightsTooltip = label.replace(
          "%{nights}",
          this.#formatNights(maxStage),
        )
      }
      return context
    }

    // TODO: Consider if it would be better to use some minimal reactive framework
    // just for the calendar widget as it really has to be dynamically built on the fly.
    #renderMonthGroup(group, monthStart, context) {
      group.innerHTML = `
        <div class="ui-datepicker-header">
          <div class="ui-datepicker-title"></div>
        </div>
      `
      this.#renderMonthTitle(
        group.querySelector(".ui-datepicker-title"),
        monthStart,
      )
      group.appendChild(this.#buildMonthGrid(monthStart, context))
    }

    #renderMobileCalendar(context) {
      this.#commitMobileCalendar(
        context,
        this.#availableMonths().map((monthStart) =>
          this.#buildMonthSection(monthStart, context),
        ),
      )
    }

    #commitMobileCalendar(context, sections) {
      if (this.hasMobileDateContainerTarget) {
        this.mobileDateContainerTarget.classList.toggle(
          "search--date_checkin",
          context.checkinCursor,
        )
        this.mobileDateContainerTarget.classList.toggle(
          "search--date_checkout",
          !context.checkinCursor,
        )
      }
      if (this.hasStickyWeekdaysTarget) {
        this.stickyWeekdaysTarget.replaceChildren(
          ...this.#weekdayHeaders().map((name) =>
            this.#createDiv(
              "sf-datepicker--cell sf-datepicker--cell_weekday",
              name,
            ),
          ),
        )
      }
      this.calendarTarget.replaceChildren(...sections)
      window.HB?.Tooltip?.init?.()
    }

    #buildMonthSection(monthStart, context) {
      const month = this.#createDiv("ui-datepicker ui-widget")
      const header = this.#createDiv(
        "ui-datepicker-header ui-widget-header ui-helper-clearfix ui-corner-all",
      )
      const title = this.#createDiv("ui-datepicker-title")
      this.#renderMonthTitle(title, monthStart)
      header.appendChild(title)
      month.appendChild(header)
      month.appendChild(
        this.#buildMonthGrid(monthStart, context, { includeWeekdays: false }),
      )
      return month
    }

    #scrollToSelectedMonth() {
      if (!this.mobileOverlayValue || !this.hasCalendarTarget) return
      const target = this.#checkinDate() || this.#minCheckinDate()
      const monthsFromStart =
        HB.UTCDate.monthKey(HB.UTCDate.firstOfMonth(target)) -
        HB.UTCDate.monthKey(HB.UTCDate.firstOfMonth(this.#minCheckinDate()))
      requestAnimationFrame(() => {
        if (monthsFromStart < 1) {
          this.calendarTarget.closest(".overlay-body")?.scrollTo({ top: 0 })
          return
        }
        const monthElement = this.calendarTarget.children[monthsFromStart]
        if (!monthElement) return
        window.Viewport?.scrollTo?.(window.$(monthElement), {
          containerSelector: ".overlay-body",
          animate: false,
        })
      })
    }

    #buildMonthGrid(monthStart, context, options = {}) {
      const includeWeekdays = options.includeWeekdays !== false
      const grid = this.#createDiv(
        `sf-datepicker search--date_${
          context.checkinCursor ? "checkin" : "checkout"
        }`,
      )
      if (includeWeekdays) {
        this.#weekdayHeaders().forEach((name) => {
          grid.appendChild(
            this.#createDiv(
              "sf-datepicker--cell sf-datepicker--cell_weekday",
              name,
            ),
          )
        })
      }
      const monthDates = this.#calendarDates(monthStart)
      const month = monthStart.getUTCMonth()
      monthDates.forEach((date) => {
        const cell = this.#createDiv(
          "sf-datepicker--cell sf-datepicker--cell_day",
        )
        if (!date || date.getUTCMonth() !== month) {
          grid.appendChild(cell)
          return
        }
        const available = this.#dateAvailable(date, context)
        this.#applyDayState(cell, date, context, available)
        this.#renderDayContent(cell, date, context, available)
        grid.appendChild(cell)
      })
      return grid
    }

    #applyDayState(cell, date, context, available) {
      const isCheckin =
        context.checkin && HB.UTCDate.sameDate(context.checkin, date)
      const isCheckout =
        context.checkout && HB.UTCDate.sameDate(context.checkout, date)
      if (available) {
        cell.classList.add("sf-datepicker--cell_selectable")
        cell.dataset.date = HB.UTCDate.formatIso(date)
      } else {
        cell.classList.add("sf-datepicker--cell_unselectable")
      }
      if (isCheckin) {
        cell.classList.add("sf-datepicker--cell_selected")
        cell.classList.add(
          context.checkinCursor
            ? "sf-datepicker--cell_current"
            : "sf-datepicker--cell_opposite",
        )
      } else if (isCheckout) {
        if (context.checkoutConfirmed) {
          cell.classList.add("sf-datepicker--cell_selected")
        }
        cell.classList.add(
          context.checkinCursor
            ? "sf-datepicker--cell_opposite"
            : "sf-datepicker--cell_current",
        )
      }
      if (!available && !isCheckin && !isCheckout) return
      if (this.#isInRange(date, context)) {
        cell.classList.add("sf-datepicker--cell_range")
      }
    }

    #renderDayContent(cell, date, context, available) {
      cell.replaceChildren()
      const unavailableTooltip = this.#maxNightsTooltip(
        date,
        context,
        available,
      )
      const nights = this.#nightsForTooltip(date, context, available)
      if (!nights && !unavailableTooltip) {
        cell.textContent = String(date.getUTCDate())
        return
      }

      const container = document.createElement("div")
      container.className =
        "tooltip--container tooltip--container_clickable sf-datepicker--tooltip--container"

      const title = document.createElement("div")
      title.className = "tooltip--title sf-datepicker--tooltip"
      title.textContent = String(date.getUTCDate())

      const tooltip = document.createElement("div")
      tooltip.className = [
        "tooltip",
        "tooltip_datepicker",
        "tooltip_nodelay",
        unavailableTooltip ? "tooltip_datepicker-unavailable" : null,
      ]
        .filter(Boolean)
        .join(" ")
      tooltip.textContent = unavailableTooltip || this.#formatNights(nights)

      container.appendChild(title)
      container.appendChild(tooltip)
      cell.appendChild(container)
    }

    #nightsForTooltip(date, context, available) {
      if (!available) return null
      if (context.checkinCursor) {
        return HB.UTCDate.distanceInNights(
          date,
          context.checkout,
          context.maxStage,
        )
      }
      return HB.UTCDate.distanceInNights(
        context.checkin,
        date,
        context.maxStage,
      )
    }

    #maxNightsTooltip(date, context, available) {
      if (available) return null
      if (!context.maxNightsTooltip) return null
      if (date <= context.maxCheckoutByStage) return null
      return context.maxNightsTooltip
    }

    #formatNights(nights) {
      const locale = this.localeValue || window.locale || "en"
      if (window.HB?.pluralize) {
        return window.HB.pluralize(nights, this.nightsValue, locale)
      }
      return `${nights} nights`
    }

    #selectDate(date) {
      if (this.checkinCursorValue) {
        this.checkinValue = HB.UTCDate.formatIso(date)
        this.checkoutValue = HB.UTCDate.formatIso(HB.UTCDate.addDays(date, 1))
        this.checkoutConfirmedValue = false
        this.checkinCursorValue = false
        this.activeTabValue = "checkout"
      } else {
        this.checkoutConfirmedValue = true
        this.checkinCursorValue = true
        this.activeTabValue = "checkin"
        this.checkoutValue = HB.UTCDate.formatIso(date)
        this.#syncInputs()
        this.requestClose()
      }
    }

    #createDiv(className, content = null) {
      const element = document.createElement("div")
      element.className = className
      if (content != null) {
        element.textContent = content
      }
      return element
    }

    #syncInputs() {
      const checkin = this.#checkinDate()
      const checkout = this.#checkoutDate()
      this.checkinInputTarget.value = checkin
        ? HB.UTCDate.formatIso(checkin)
        : ""
      this.checkoutInputTarget.value = checkout
        ? HB.UTCDate.formatIso(checkout)
        : ""
      this.#syncCalendarDisplay(
        this.hasCheckinValueTarget ? this.checkinValueTarget : null,
        this.hasCheckinLabelTarget ? this.checkinLabelTarget : null,
        checkin,
      )
      this.#syncCalendarDisplay(
        this.hasCheckoutValueTarget ? this.checkoutValueTarget : null,
        this.hasCheckoutLabelTarget ? this.checkoutLabelTarget : null,
        checkout,
      )
      this.checkinFieldTarget.classList.toggle(
        "datepicker-defined",
        Boolean(checkin),
      )
      this.checkoutFieldTarget.classList.toggle(
        "datepicker-defined",
        Boolean(checkout),
      )
      this.#syncMobileFields(checkin, checkout)
      this.#scheduleHeading(checkin, checkout)
    }

    #scheduleHeading(checkin, checkout) {
      if (this.#overlayHidden()) {
        this.#headingScheduled = true
        this.#armDeferredWork()
        return
      }
      this.#syncHeading(checkin, checkout)
    }

    #flushHeading() {
      if (!this.#headingScheduled) return
      this.#headingScheduled = false
      this.#syncHeading()
    }

    #syncCalendarDisplay(valueTarget, labelTarget, date) {
      if (!valueTarget || !labelTarget) return
      valueTarget.innerHTML = date
        ? this.#formatDisplay(date)
        : labelTarget.textContent
      labelTarget.hidden = !date
    }

    #syncMobileFields(checkin, checkout) {
      if (!this.mobileOverlayValue) return
      const checkinText = checkin ? this.#formatDisplay(checkin) : ""
      if (this.hasMobileCheckinLabelTarget) {
        this.mobileCheckinLabelTarget.hidden = !checkinText
      }
      if (this.hasMobileCheckinValueTarget) {
        this.mobileCheckinValueTarget.innerHTML = checkinText
        this.mobileCheckinValueTarget.hidden = !checkinText
      }
      if (this.hasMobileCheckinPlaceholderTarget) {
        this.mobileCheckinPlaceholderTarget.hidden = Boolean(checkinText)
      }

      const checkoutText = checkout ? this.#formatDisplay(checkout) : ""
      if (this.hasMobileCheckoutLabelTarget) {
        this.mobileCheckoutLabelTarget.hidden = !checkoutText
      }
      if (this.hasMobileCheckoutValueTarget) {
        this.mobileCheckoutValueTarget.innerHTML = checkoutText
        this.mobileCheckoutValueTarget.hidden = !checkoutText
      }
      if (this.hasMobileCheckoutPlaceholderTarget) {
        this.mobileCheckoutPlaceholderTarget.hidden = Boolean(checkoutText)
      }

      if (this.hasMobileCheckinFieldTarget) {
        this.mobileCheckinFieldTarget.classList.toggle(
          "opened",
          this.checkinCursorValue,
        )
      }
      if (this.hasMobileCheckoutFieldTarget) {
        this.mobileCheckoutFieldTarget.classList.toggle(
          "opened",
          !this.checkinCursorValue,
        )
      }
    }

    #dateAvailable(date, context) {
      if (!context.availableFrom) return false
      if (date < context.availableFrom) return false
      return context.availableToInclusive
        ? date <= context.availableTo
        : date < context.availableTo
    }

    #isInRange(date, context) {
      if (!context.checkin || !context.checkout) return false
      return date > context.checkin && date < context.checkout
    }

    #formatDisplay(date) {
      const dayName = this.dayNamesMediumValue[date.getUTCDay()]
      const MY = this.#formatPattern(date, {
        includeYear: !this.#isArabicLocale(),
      })

      return (
        `<span class="comma-separated">${dayName}</span>` +
        `<span class="comma-separated">${MY}</span>`
      )
    }

    #formatPattern(date, options = {}) {
      const includeYear = options.includeYear !== false
      let pattern = this.datePatternValue || "%-d %b %Y"
      if (!includeYear) {
        pattern = pattern
          .replace(/%Y/g, "")
          .replace(/%y/g, "")
          .replace(/\s{2,}/g, " ")
          .trim()
      }
      const day = String(date.getUTCDate())
      const year = String(date.getUTCFullYear())
      const monthIndex = date.getUTCMonth() + 1
      const monthAbbr = this.monthsAbbreviatedValue[monthIndex]
      const monthFull = this.monthsValue[monthIndex]
      return pattern
        .replace(/%-?d/g, day)
        .replace(/%-?e/g, day)
        .replace(/%b/g, monthAbbr || "")
        .replace(/%B/g, monthFull || "")
        .replace(/%Y/g, year)
        .replace(/%y/g, year.slice(-2))
        .replace(/\s{2,}/g, " ")
        .trim()
        .replace(/[\/.,،-]\s*$/, "")
        .trim()
    }

    #calendarDates(monthStart) {
      const start = this.#gridStart(monthStart)
      return Array.from({ length: 42 }, (_item, idx) =>
        HB.UTCDate.addDays(start, idx),
      )
    }

    #gridStart(monthStart) {
      const weekday = HB.UTCDate.dayOfWeek(monthStart)
      return HB.UTCDate.addDays(monthStart, -(weekday - 1))
    }

    #renderMonthTitle(titleElement, date) {
      if (!titleElement) return
      titleElement.replaceChildren()
      const isArabic = String(this.localeValue || window.locale || "en")
        .toLowerCase()
        .startsWith("ar")
      if (isArabic) {
        titleElement.setAttribute("dir", "rtl")
      } else {
        titleElement.removeAttribute("dir")
      }
      this.#monthYearParts(date).forEach((part) => {
        if (part.type === "literal") {
          titleElement.appendChild(document.createTextNode(part.value))
          return
        }
        const partElement = document.createElement("span")
        partElement.className = `ui-datepicker-title-${part.type}`
        partElement.textContent = part.value
        if (part.type === "year") {
          partElement.setAttribute("dir", "ltr")
        } else if (part.type === "month" && isArabic) {
          partElement.setAttribute("dir", "rtl")
        }
        titleElement.appendChild(partElement)
      })
    }

    #monthYearParts(date) {
      const monthName = this.monthsValue[date.getUTCMonth() + 1] || ""
      const year = String(date.getUTCFullYear())
      if (this.#isArabicLocale()) {
        return [
          { type: "year", value: year },
          { type: "literal", value: " " },
          { type: "month", value: monthName },
        ]
      }
      return [
        { type: "month", value: monthName },
        { type: "literal", value: " " },
        { type: "year", value: year },
      ]
    }

    #isArabicLocale() {
      return String(this.localeValue || window.locale || "en")
        .toLowerCase()
        .startsWith("ar")
    }

    #syncHeading(
      checkin = this.#checkinDate(),
      checkout = this.#checkoutDate(),
    ) {
      if (!this.mobileOverlayValue || !this.hasHeadingTarget) return
      const maxStage = this.maxStageDurationValue || 0
      const nights =
        HB.UTCDate.distanceInNights(checkin, checkout, maxStage) || null
      const heading = nights ? this.#formatNights(nights) : this.datesLabelValue
      this.headingTarget.textContent = heading
      if (
        this.#isOverlayPane() &&
        this.paneTarget.classList.contains("overlay_opened")
      ) {
        this.#postTitleToNative()
      }
    }

    #checkinDate() {
      return HB.UTCDate.parse(this.checkinValue)
    }

    #checkoutDate() {
      return HB.UTCDate.parse(this.checkoutValue)
    }

    #monthStartDate() {
      return HB.UTCDate.parse(this.monthStartValue)
    }

    #minCheckinDate() {
      return HB.UTCDate.parse(this.minCheckinValue) || HB.UTCDate.today()
    }

    #weekdayHeaders(source = this.dayNamesValue) {
      const names = source || []
      if (names.length !== 7) return names
      return names.slice(1).concat(names[0])
    }

    #availableMonths() {
      const firstMonth = HB.UTCDate.firstOfMonth(this.#minCheckinDate())
      const lastMonth = HB.UTCDate.firstOfMonth(
        HB.UTCDate.addDays(this.#minCheckinDate(), this.maxLeadDaysValue || 0),
      )
      const months = []
      let current = firstMonth
      while (HB.UTCDate.monthKey(current) <= HB.UTCDate.monthKey(lastMonth)) {
        months.push(current)
        current = HB.UTCDate.addMonths(current, 1)
      }
      return months
    }

    #isOverlayPane() {
      return (
        this.mobileOverlayValue && this.paneTarget.classList.contains("overlay")
      )
    }

    #canNavigatePrev(monthStart) {
      const minMonth = HB.UTCDate.firstOfMonth(this.#minCheckinDate())
      return HB.UTCDate.monthKey(monthStart) > HB.UTCDate.monthKey(minMonth)
    }

    #canNavigateNext(monthStart) {
      const maxLead = this.maxLeadDaysValue || 0
      const maxMonth = HB.UTCDate.firstOfMonth(
        HB.UTCDate.addDays(this.#minCheckinDate(), maxLead),
      )
      return HB.UTCDate.monthKey(monthStart) < HB.UTCDate.monthKey(maxMonth)
    }

    #syncNav(monthStart) {
      if (this.hasPrevButtonTarget) {
        const canPrev = this.#canNavigatePrev(monthStart)
        this.prevButtonTarget.classList.toggle("ui-state-disabled", !canPrev)
        this.prevButtonTarget.setAttribute("aria-disabled", String(!canPrev))
      }
      if (this.hasNextButtonTarget) {
        const canNext = this.#canNavigateNext(monthStart)
        this.nextButtonTarget.classList.toggle("ui-state-disabled", !canNext)
        this.nextButtonTarget.setAttribute("aria-disabled", String(!canNext))
      }
    }

    #navigate(event, delta) {
      event.preventDefault()
      const monthStart = this.#monthStartDate()
      if (!monthStart) return
      if (delta < 0 && !this.#canNavigatePrev(monthStart)) return
      if (delta > 0 && !this.#canNavigateNext(monthStart)) return
      const nextMonth = HB.UTCDate.addMonths(monthStart, delta)
      this.monthStartValue = HB.UTCDate.formatIso(nextMonth)
    }

    #postTitleToNative() {
      if (this.hasHeadingTarget) {
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ title: this.headingTarget.textContent }),
        )
      }
    }
  },
)
